import { AlertTriangle } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDiceRegistryStore } from '../stores/diceRegistryStore';
import { useErrorLogStore } from '../stores/errorLogStore';
import { AccountSection } from '@/components/AccountSection';
import { AIModelSettings } from '@/components/AIModelSettings';
import { AppVersion } from '@/components/AppVersion';
import { DiceRegistrationList } from '@/components/DiceRegistrationList';
import { StorageSettings } from '@/components/StorageSettings';
import { DiceTypeSettings } from '@/components/dice-settings/DiceTypeSettings';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible } from '@/components/ui/collapsible';
import { PageHeader } from '@/components/PageHeader';
import { PAGE_TITLES } from '@/constants/terminology';
import { useAuthStore } from '@/stores/authStore';

const SERVICE_TAG = '[SETTINGS]';

/**
 * Settings page for AI, dice registration, storage, and advanced options
 *
 * Connection management has moved to the Portal Chamber (HomePage).
 * This page now focuses on non-connection settings.
 */
export function SettingsPage() {
  const navigate = useNavigate();
  const { deviceId, setDeviceId } = useDiceRegistryStore();
  const { unacknowledgedCount } = useErrorLogStore();
  const { isAuthenticated, isGuest, logout } = useAuthStore();

  /** Sign out and redirect to login page */
  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  // Generate or retrieve device ID on mount
  useEffect(() => {
    if (!deviceId) {
      const newDeviceId = crypto.randomUUID();
      setDeviceId(newDeviceId);
      console.info(`${SERVICE_TAG} Generated new device ID`, { deviceId: newDeviceId });
    }
  }, [deviceId, setDeviceId]);

  return (
    <div className="flex h-full flex-col p-4 overflow-y-auto">
      <div className="w-full max-w-card mx-auto min-h-0">
        <PageHeader variant="settings" title={PAGE_TITLES.SETTINGS} />

        {/* Account Section */}
        <AccountSection onLogout={handleLogout} />

        {/* Guest account prompt */}
        {isGuest && !isAuthenticated && (
          <div className="mb-4 p-4 rounded-lg border border-border/50 bg-card/50">
            <p className="text-sm text-muted-foreground">
              Create an account to save your data across devices.{' '}
              <button
                type="button"
                onClick={() => navigate('/login', { state: { mode: 'register' } })}
                className="text-primary hover:underline font-medium"
              >
                Sign up
              </button>
            </p>
          </div>
        )}

        {/* AI Model Settings - Expanded by default */}
        <Collapsible title="AI Recognition" defaultOpen className="mb-4">
          <AIModelSettings embedded />
        </Collapsible>

        {/* Dice Registration - Expanded by default */}
        <Collapsible title="Dice Registration" defaultOpen className="mb-4">
          <p className="text-sm text-muted-foreground mb-4">
            Train the system to recognize your custom dice for improved accuracy.
          </p>
          <Button
            variant="accent"
            className="w-full mb-4"
            onClick={() => navigate('/register')}
            enablePressEffect
          >
            Register New Dice
          </Button>
          <DiceRegistrationList />
        </Collapsible>

        {/* My Dice - Provider priority per dice type */}
        <Collapsible
          title="My Dice"
          description="Configure recognition providers for each registered dice type"
          defaultOpen={false}
          className="mb-4"
        >
          <DiceTypeSettings />
        </Collapsible>

        {/* Storage Settings - Collapsed by default (one-time setup) */}
        {deviceId && (
          <Collapsible
            title="Storage & Backup"
            description="Configure where dice registration images are saved"
            defaultOpen={false}
            className="mb-4"
          >
            <StorageSettings deviceId={deviceId} embedded />
          </Collapsible>
        )}

        {/* Advanced Settings - Collapsed by default (rarely used) */}
        <Collapsible
          title="Advanced"
          description="Expert settings and troubleshooting"
          defaultOpen={false}
          className="mb-4"
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Error Log
              </label>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/error-log')}
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                View Error Log
                {unacknowledgedCount > 0 && (
                  <Badge variant="destructive" className="ml-auto">
                    {unacknowledgedCount}
                  </Badge>
                )}
              </Button>
              <p className="text-xs text-muted-foreground">
                View history of errors and warnings for troubleshooting.
              </p>
            </div>
          </div>
        </Collapsible>

        <footer className="py-4 text-center text-xs text-muted-foreground">
          <div className="h-px w-24 mx-auto mb-4 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          <AppVersion />
        </footer>
      </div>
    </div>
  );
}
